import React, { useState, useRef } from 'react';
import { Glyphicon } from 'react-bootstrap';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { toggleIsOpen as toggleIsOpenAction, saveGroupElement as saveGroupElementAction } from '../../../../actions/reduxActions/groupElements';
import { ungroup as ungroupAction } from '../../../../actions/reduxActions/graphEditor';
import { getElementStyles } from '../elementStyles';
import '../../../../../css/graph/group-element.less';
import { MAP_ELEMENT_TYPES, keyCodes } from '../../../../constants';
import { path } from 'ramda';
import { addNotification as addNotificationAction } from '../../../../actions/reduxActions/notification';
import { LinkBreak } from 'phosphor-react';

export const GroupElementBase = ({
    onOver,
    onOut,
    onDoubleClick,
    onDelete,
    isSelected,
    isHovered,
    resizeHandles,
    isDragging,
    draggingOver,
    height,
    width,
    x,
    y,
    developerName: initialDeveloperName,
    elementType,
    id,
    flowId,
    editingToken,
    groupRef,
    startDrag,
    onSelect,
    currentUser,
    saveGroupElement,
    groupElement,
    addNotification,
    ungroup,
}) => {
    const [editingName, setEditingName] = useState(false);
    const [editMouseDown, setEditMouseDown] = useState(false);
    const nameRef = useRef(null);
    
    const { tenantId, authenticationToken } = currentUser;

    const draggingProps = editingName ? {} : {
        onMouseDown: startDrag,
        onMouseUp: onSelect,
        onMouseEnter: onOver,
        onFocus: onOver,
        onDoubleClick,
    };

    const styles = getElementStyles(elementType);

    const className = classnames({
        hover: isHovered,
        drag: isDragging,
        select: isSelected,
    });

    const debouncedSave = manywho.utils.debounce(saveGroupElement, 750);

    const saveName = () => {
        setEditingName(false);
        setEditMouseDown(false);
        if (nameRef.current.textContent) {
            // Only save if they changed the name
            if (nameRef.current.textContent !== initialDeveloperName) {
                debouncedSave(
                    { ...groupElement, developerName: nameRef.current.textContent },
                    flowId,
                    editingToken,
                    tenantId,
                    authenticationToken,
                );
            }
        } else {
            // Using the ref to set the content-editable span's content
            // because React does not update content-editable components correctly
            nameRef.current.textContent = initialDeveloperName;
            addNotification({
                type: 'error',
                message: 'Groups cannot be given an empty name',
                isPersistent: true,
            });
        }
    };

    const onUngroup = () => ungroup({
        flowId,
        editingToken,
        selectedGroupElementId: id,
    });

    return (
        <g
            transform={`matrix(1,0,0,1,${x},${y})`}
            ref={groupRef}
            onMouseLeave={onOut}
            height={height}
            width={width}
            className={className}
        >
            {/* Grey hover/selection border to help indicate selection/focus */}
            {
                (isSelected) && !isDragging && (
                    <rect 
                        x={-5}
                        y={-5}
                        width={width + (5 * 2)}
                        height={height + (5 * 2)}
                        stroke="#9c9c9c"
                        rx={1}
                        strokeWidth={0.7}
                        strokeDasharray={2}
                        fillOpacity={0}
                        className="map-element-hover-out"
                    />
                )
            }
            {/* Border around the whole group */}
            <rect
                stroke={styles.border.fill}
                strokeOpacity={1}
                rx={1}
                strokeWidth={0.7}
                fill="rgba(0,0,0,0)"
                height={height}
                width={width}
            />
            <g 
                onMouseDown={startDrag}
                onMouseUp={onSelect}
                onMouseEnter={onOver}
                onFocus={onOver}
                onDoubleClick={onDoubleClick}
            >
                {/* Square bottom to group header */}
                <rect
                    fill={styles.border.fill}
                    y={20}
                    height={10}
                    width={width}
                    className="group-element-drag-handle"
                />
                {/* Rounded header */}
                <rect
                    rx={1}
                    fill={styles.border.fill}
                    height={30}
                    width={width}
                    className="group-element-drag-handle"
                />
            </g>
            {/* Green dragging square to highlight when a map element is being dragging into this group */}
            {draggingOver && <rect strokeWidth="2" stroke="#0f0" fill="rgba(0,0,0,0)" x="-2" y="-2" height={height + 4} width={width + 4} />}
            {/* Group name, icon and content */}
            <foreignObject
                x={5}
                y={5}
                width={width - 10}
                height={20}
                {...draggingProps}
                className="group-element-drag-handle"
            >
                <span className="map-element-content">
                    <Glyphicon className={`map-element-icon ${elementType === MAP_ELEMENT_TYPES.swimlane ? '' : 'map-element-no-actions'}`} glyph={styles.icon} />
                    <span className={`group-element-text ${editingName && 'editing'}`}>
                        <span
                            ref={nameRef}
                            role="textbox"
                            tabIndex="-1"
                            // If they mouse down on this after selection
                            onMouseDown={() => { if (isSelected) { setEditMouseDown(true); } }}
                            // and then mouse up on this after that, then we can be sure they want to edit the name
                            onMouseUp={() => { if (editMouseDown) { setEditingName(true); setEditMouseDown(false); } }}
                            onBlur={saveName}
                            onKeyDown={e => e.keyCode === keyCodes.ENTER ? saveName() : null}
                            // Stop propagation so DELETE key doesn't delete group
                            onKeyUp={e => e.stopPropagation()}
                            // Stop propagation so clicking to edit doesn't edit group
                            onDoubleClick={e => e.stopPropagation()}
                            // If they move the mouse during editing, then stop propagation of that event so the element doesn't move
                            // If they move the mouse after mouse down, then unset mouse down, as they probably want to drag and not edit
                            onMouseMove={(e) => { if (editingName) { e.stopPropagation(); } setEditMouseDown(false); }}
                            className={`map-element-developer-name ${elementType === MAP_ELEMENT_TYPES.swimlane ? '' : 'map-element-no-actions'}`}
                            title={path(['current', 'textContent'], nameRef)}
                            contentEditable={isSelected && editingName}
                        >
                            {initialDeveloperName}
                        </span>
                    </span>
                </span>
            </foreignObject>
            {/* Resize handles for scaling the group */}
            {resizeHandles}
            {/* Delete button */}
            {
                (isSelected) && !isDragging && (
                    <foreignObject {...styles.iconRow} x={width - styles.iconRow.width + 5} y={-10}>
                        <span className="group-element-delete-background" />
                        <Glyphicon
                            onClick={onDelete}
                            className="group-element-icon group-element-delete"
                            glyph="remove-sign"
                        />
                        {elementType === MAP_ELEMENT_TYPES.group && <>
                            <LinkBreak
                                onClick={onUngroup}
                                className="group-element-icon"
                                weight="bold"
                                style={{ 
                                    top: '-1px',
                                    left: '15px',
                                    fontSize: '12px',
                                    position: 'absolute',
                                    background: '#f5f5f5',
                                    cursor: 'pointer',
                                }}
                            />
                        </>}
                    </foreignObject>
                )
            }
        </g>
    );
};

export default connect(
    ({ currentUser }) => ({ currentUser }),
    {
        toggleIsOpen: toggleIsOpenAction,
        saveGroupElement: saveGroupElementAction,
        addNotification: addNotificationAction,
        ungroup: ungroupAction,
    },
)(GroupElementBase);
